/**
 * Mobile filter sheet/modal presentation (Figma redesign).
 * Six collapsible sections: BASIC, FINANCIAL, FIT & RATINGS, PERFORMANCE,
 * GOALKEEPER, REPORT STATS.
 * Supports two presentation modes: "actionsheet" (bottom sheet on the shared
 * MobileSheetChrome) and "modal" (full-screen Modal atom).
 * Uses MobileMultiSelect for searchable multi-select dropdowns (consistent with web FilterSheet).
 * @module components/shared/MobileFilterSheet/MobileFilterSheetView
 */

import { ChevronDown, ChevronUp, X } from "lucide-react-native";
import React, { memo } from "react";

import MobileSheetChrome from "@/components/molecules/MobileSheetChrome";
import { TEST_ID_WATCHLIST } from "e2e/testId";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import ActiveFilterCountBadge from "@/components/organisms/shared/ActiveFilterCountBadge";
import MobileMultiSelect from "./MobileMultiSelect";
import MobileRoleChip from "./MobileRoleChip";
import ScoreFilterSections from "./ScoreFilterSections";
import RangeFilterField from "@/components/organisms/shared/RangeFilterField";

import type { FilterOption } from "@/types/filter";
import type { SectionKey } from "@/features/scouting/utils/countSectionFilters";
import type { FilterSheetPresentation } from "./MobileFilterSheetContainer";

import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_VALUATION_RANGE,
} from "@/constants/filterDefaults";
import formatNumberWithCommas from "@/utils/formatNumberWithCommas";
import LeaguePresetControls from "@/features/player-filter/components/LeaguePresetControls";
import type { LeaguePresetOption } from "@/features/player-filter/utils/leaguePresets";

/**
 * Chunks an array into pairs for 2-column grid layout
 * @param items - Array to chunk into pairs
 */
const chunkPairs = <T,>(
  items: readonly T[]
): readonly (readonly [T, T | undefined])[] =>
  Array.from(
    { length: Math.ceil(items.length / 2) },
    (_, i) => [items[i * 2], items[i * 2 + 1]] as const
  );

/** Number of role chips visible before "Show more" */
const ROLE_VISIBLE_COUNT = 6;

/**
 * Builds the primary footer button label, appending the live result count
 * when available (SE-5082): "Show Results" → "Show Results (42)". The count is
 * parenthesised to match the Figma design (SE-4146).
 * @param count - Live count of matching players, or null when unavailable
 * @returns The footer button label
 */
const showResultsLabel = (count: number | null): string =>
  count === null
    ? "Show Results"
    : `Show Results (${formatNumberWithCommas(count)})`;

/** Props for MobileFilterSheetView */
interface MobileFilterSheetViewProps {
  readonly presentation?: FilterSheetPresentation;
  /**
   * Safe-area top inset (px) — applied as top padding so the modal header
   *  clears the status bar.
   */
  readonly topInset?: number;
  /**
   * Safe-area bottom inset (px) — applied as bottom padding so the modal
   *  footer clears the system navigation bar.
   */
  readonly bottomInset?: number;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onClearAll: () => void;
  readonly onApply: () => void;
  /** Total active filter count — drives CLEAR ALL disabled state */
  readonly activeFilterCount: number;
  /**
   * Live count of players matching the current filter state, rendered as
   * "Show Results [N]" in the footer (SE-5082). `null` hides the count.
   */
  readonly resultCount: number | null;
  /** Disables the primary "Show Results" button (SE-4146 AC#3). */
  readonly applyDisabled: boolean;
  /** Optional testID for the footer "Discard"/"Cancel" button (e2e). */
  readonly discardTestID?: string;
  /** Optional testID for the footer "Show Results" primary button (e2e). */
  readonly showResultsTestID?: string;
  /** Per-section active filter counts for badge display */
  readonly sectionCounts: Readonly<Record<SectionKey, number>>;

  /* --- Section toggle state --- */
  readonly isBasicOpen: boolean;
  readonly onToggleBasic: () => void;
  readonly isFinancialOpen: boolean;
  readonly onToggleFinancial: () => void;
  readonly isFitRatingsOpen: boolean;
  readonly onToggleFitRatings: () => void;
  readonly isPerformanceOpen: boolean;
  readonly onTogglePerformance: () => void;
  readonly isGoalkeeperOpen: boolean;
  readonly onToggleGoalkeeper: () => void;

  /* --- Role chips --- */
  readonly roleOptions: readonly FilterOption[];
  readonly selectedRoleArchetypes: readonly string[];
  readonly onToggleRole: (id: string) => void;
  readonly onClearRoles: () => void;
  readonly isRoleExpanded: boolean;
  readonly onToggleRoleExpanded: () => void;

  /* --- Multi-select dropdowns --- */
  readonly positionOptions: readonly FilterOption[];
  readonly selectedPositionIds: readonly string[];
  readonly onTogglePosition: (id: string) => void;
  readonly onClearPositions: () => void;

  readonly leagueOptions: readonly LeaguePresetOption[];
  readonly selectedLeagueIds: readonly string[];
  readonly onToggleLeague: (id: string) => void;
  readonly onClearLeagues: () => void;
  readonly onChangeLeaguePreset?: (ids: readonly string[]) => void;

  readonly competitionOptions: readonly FilterOption[];
  readonly selectedCompetitionIds: readonly string[];
  readonly onToggleCompetition: (id: string) => void;
  readonly onClearCompetitions: () => void;

  readonly clubOptions: readonly FilterOption[];
  readonly selectedClubIds: readonly string[];
  readonly onToggleClub: (id: string) => void;
  readonly onClearClubs: () => void;

  readonly nationalityOptions: readonly FilterOption[];
  readonly selectedNationalityIds: readonly string[];
  readonly onToggleNationality: (id: string) => void;
  readonly onClearNationalities: () => void;

  /* --- Club server-side search --- */
  /** Current club search text for server-side filtering */
  readonly clubSearchText: string;
  /** Callback to update the club search text */
  readonly onClubSearchChange: (text: string) => void;
  /** Whether club search query is loading */
  readonly clubSearching: boolean;

  /* --- Range filters --- */
  readonly ageRange: [number, number];
  readonly onSetAgeRange: (range: [number, number]) => void;
  readonly valuationRange: [number, number];
  readonly onSetValuationRange: (range: [number, number]) => void;
  readonly contractMonthsRange: [number, number];
  readonly onSetContractMonthsRange: (range: [number, number]) => void;

  /* --- Fit & Ratings --- */
  readonly gprRange: [number, number];
  readonly onSetGprRange: (range: [number, number]) => void;
  readonly fitScoreRange: [number, number];
  readonly onSetFitScoreRange: (range: [number, number]) => void;
  readonly teamStyleFitRange: [number, number];
  readonly onSetTeamStyleFitRange: (range: [number, number]) => void;

  /* --- Performance --- */
  readonly carryingRange: [number, number];
  readonly onSetCarryingRange: (range: [number, number]) => void;
  readonly dribblingRange: [number, number];
  readonly onSetDribblingRange: (range: [number, number]) => void;
  readonly passingRange: [number, number];
  readonly onSetPassingRange: (range: [number, number]) => void;
  readonly shootingRange: [number, number];
  readonly onSetShootingRange: (range: [number, number]) => void;

  /* --- Goalkeeper --- */
  readonly gkPassingRange: [number, number];
  readonly onSetGkPassingRange: (range: [number, number]) => void;
  readonly gkShotStoppingRange: [number, number];
  readonly onSetGkShotStoppingRange: (range: [number, number]) => void;
  readonly gkPositioningRange: [number, number];
  readonly onSetGkPositioningRange: (range: [number, number]) => void;

  /* --- Report Stats --- */
  readonly isReportStatsOpen: boolean;
  readonly onToggleReportStats: () => void;
  readonly customMetrics: readonly {
    readonly id: string;
    readonly displayName: string;
  }[];
  readonly customStatRanges: Readonly<
    Record<string, readonly [number, number]>
  >;
  readonly onSetCustomStatRange: (
    name: string,
    range: [number, number]
  ) => void;
}

/** Section border className for theme-aware borders */
const SECTION_BORDER_CLASS =
  "border-b border-outline-default dark:border-needs-semantic-outline-800";

/**
 * Renders role chips and their clear/expansion controls.
 * @param p - Filter sheet props
 */
const renderRoleFilters = (p: MobileFilterSheetViewProps) => (
  <Stack className="gap-2">
    <Stack direction="horizontal" className="items-center justify-between">
      <Text variant="unstyled" className="text-title-sm text-content-primary">
        Roles
      </Text>
      {p.selectedRoleArchetypes.length > 0 ? (
        <Pressable onPress={p.onClearRoles}>
          <Text variant="caption" className="text-accent-primary">
            Clear
          </Text>
        </Pressable>
      ) : null}
    </Stack>
    <Stack className="gap-2">
      {chunkPairs(
        p.isRoleExpanded
          ? p.roleOptions
          : p.roleOptions.slice(0, ROLE_VISIBLE_COUNT)
      ).map(pair => (
        <Stack direction="horizontal" key={pair[0].id} className="gap-2">
          <MobileRoleChip
            id={pair[0].id}
            label={pair[0].label}
            isSelected={p.selectedRoleArchetypes.includes(pair[0].id)}
            onToggle={p.onToggleRole}
            testID={TEST_ID_WATCHLIST.FILTER_ROLE_OPTION}
          />
          {pair[1] ? (
            <MobileRoleChip
              id={pair[1].id}
              label={pair[1].label}
              isSelected={p.selectedRoleArchetypes.includes(pair[1].id)}
              onToggle={p.onToggleRole}
              testID={TEST_ID_WATCHLIST.FILTER_ROLE_OPTION}
            />
          ) : (
            <Stack className="flex-1" />
          )}
        </Stack>
      ))}
    </Stack>
    {p.roleOptions.length > ROLE_VISIBLE_COUNT ? (
      <Pressable onPress={p.onToggleRoleExpanded} className="items-center py-1">
        <Text variant="caption" className="text-accent-primary">
          {p.isRoleExpanded ? "Show less" : "Show more"}
        </Text>
      </Pressable>
    ) : null}
  </Stack>
);

/**
 * Renders the shared filter sections (BASIC, FINANCIAL, score sections)
 * used by both actionsheet and modal presentation modes.
 * @param p - Filter sheet props
 */
const renderFilterSections = (p: MobileFilterSheetViewProps) => (
  <>
    {/* ---- BASIC section ---- */}
    <Stack className={SECTION_BORDER_CLASS}>
      <Pressable
        onPress={p.onToggleBasic}
        className="flex-row items-center justify-between py-3"
      >
        <Stack direction="horizontal" className="items-center gap-2">
          <Text variant="title-sm" className="uppercase text-content-primary">
            BASIC
          </Text>
          <ActiveFilterCountBadge count={p.sectionCounts.basic} />
        </Stack>
        <Icon
          as={p.isBasicOpen ? ChevronUp : ChevronDown}
          size="sm"
          className="text-content-subtle"
        />
      </Pressable>

      {p.isBasicOpen ? (
        <Stack className="gap-4 pb-4">
          {/* Roles chip grid */}
          {renderRoleFilters(p)}

          {/* Multi-select dropdowns (consistent with web FilterSheet) */}
          <MobileMultiSelect
            label="Positions"
            placeholder="All Positions"
            options={p.positionOptions}
            selectedIds={p.selectedPositionIds}
            onToggle={p.onTogglePosition}
            onClear={p.onClearPositions}
            isDark={p.isDark}
          />
          <MobileMultiSelect
            label="Leagues"
            placeholder="All Leagues"
            options={p.leagueOptions}
            selectedIds={p.selectedLeagueIds}
            onToggle={p.onToggleLeague}
            onClear={p.onClearLeagues}
            isDark={p.isDark}
          />
          {p.onChangeLeaguePreset ? (
            <LeaguePresetControls
              leagueOptions={p.leagueOptions}
              selectedLeagueIds={p.selectedLeagueIds}
              onChange={p.onChangeLeaguePreset}
            />
          ) : null}
          {p.competitionOptions.length > 0 ? (
            <MobileMultiSelect
              label="Cup competitions"
              placeholder="All Cup competitions"
              options={p.competitionOptions}
              selectedIds={p.selectedCompetitionIds}
              onToggle={p.onToggleCompetition}
              onClear={p.onClearCompetitions}
              isDark={p.isDark}
            />
          ) : null}
          <MobileMultiSelect
            label="Clubs"
            placeholder="All Clubs"
            options={p.clubOptions}
            selectedIds={p.selectedClubIds}
            onToggle={p.onToggleClub}
            onClear={p.onClearClubs}
            isDark={p.isDark}
            externalSearchText={p.clubSearchText}
            onExternalSearchChange={p.onClubSearchChange}
            isSearching={p.clubSearching}
          />
          <MobileMultiSelect
            label="Nationalities"
            placeholder="All Nationalities"
            options={p.nationalityOptions}
            selectedIds={p.selectedNationalityIds}
            onToggle={p.onToggleNationality}
            onClear={p.onClearNationalities}
            isDark={p.isDark}
          />

          {/* Age range */}
          <RangeFilterField
            key="age"
            label="Age"
            value={p.ageRange}
            minValue={DEFAULT_AGE_RANGE[0]}
            maxValue={DEFAULT_AGE_RANGE[1]}
            step={1}
            onChange={p.onSetAgeRange}
            isDark={p.isDark}
          />

          {/* Months Remaining — in BASIC per web FilterSheet rules */}
          <RangeFilterField
            key="months"
            label="Months Remaining"
            value={p.contractMonthsRange}
            minValue={DEFAULT_CONTRACT_MONTHS_RANGE[0]}
            maxValue={DEFAULT_CONTRACT_MONTHS_RANGE[1]}
            step={1}
            onChange={p.onSetContractMonthsRange}
            isDark={p.isDark}
          />
        </Stack>
      ) : null}
    </Stack>

    {/* ---- FINANCIAL section ---- */}
    <Stack className={SECTION_BORDER_CLASS}>
      <Pressable
        onPress={p.onToggleFinancial}
        className="flex-row items-center justify-between py-3"
      >
        <Stack direction="horizontal" className="items-center gap-2">
          <Text variant="title-sm" className="uppercase text-content-primary">
            FINANCIAL
          </Text>
          <ActiveFilterCountBadge count={p.sectionCounts.financial} />
        </Stack>
        <Icon
          as={p.isFinancialOpen ? ChevronUp : ChevronDown}
          size="sm"
          className="text-content-subtle"
        />
      </Pressable>

      {p.isFinancialOpen ? (
        <Stack className="gap-4 pb-4">
          <RangeFilterField
            key="cost"
            label="Cost (in €)"
            value={p.valuationRange}
            minValue={DEFAULT_VALUATION_RANGE[0]}
            maxValue={DEFAULT_VALUATION_RANGE[1]}
            step={100_000}
            onChange={p.onSetValuationRange}
            isCurrency
            useLogScale
            isDark={p.isDark}
          />
        </Stack>
      ) : null}
    </Stack>

    <ScoreFilterSections
      isFitRatingsOpen={p.isFitRatingsOpen}
      onToggleFitRatings={p.onToggleFitRatings}
      gprRange={p.gprRange}
      onSetGprRange={p.onSetGprRange}
      fitScoreRange={p.fitScoreRange}
      onSetFitScoreRange={p.onSetFitScoreRange}
      teamStyleFitRange={p.teamStyleFitRange}
      onSetTeamStyleFitRange={p.onSetTeamStyleFitRange}
      isPerformanceOpen={p.isPerformanceOpen}
      onTogglePerformance={p.onTogglePerformance}
      carryingRange={p.carryingRange}
      onSetCarryingRange={p.onSetCarryingRange}
      dribblingRange={p.dribblingRange}
      onSetDribblingRange={p.onSetDribblingRange}
      passingRange={p.passingRange}
      onSetPassingRange={p.onSetPassingRange}
      shootingRange={p.shootingRange}
      onSetShootingRange={p.onSetShootingRange}
      isGoalkeeperOpen={p.isGoalkeeperOpen}
      onToggleGoalkeeper={p.onToggleGoalkeeper}
      gkPassingRange={p.gkPassingRange}
      onSetGkPassingRange={p.onSetGkPassingRange}
      gkShotStoppingRange={p.gkShotStoppingRange}
      onSetGkShotStoppingRange={p.onSetGkShotStoppingRange}
      gkPositioningRange={p.gkPositioningRange}
      onSetGkPositioningRange={p.onSetGkPositioningRange}
      isReportStatsOpen={p.isReportStatsOpen}
      onToggleReportStats={p.onToggleReportStats}
      customMetrics={p.customMetrics}
      customStatRanges={p.customStatRanges}
      onSetCustomStatRange={p.onSetCustomStatRange}
      fitRatingsFilterCount={p.sectionCounts.fitAndRatings}
      performanceFilterCount={p.sectionCounts.performance}
      goalkeeperFilterCount={p.sectionCounts.goalkeeper}
      reportStatsFilterCount={p.sectionCounts.reportStats}
      isDark={p.isDark}
    />
  </>
);

/**
 * ModalBody is a gluestack ScrollView; these make it a non-scrolling flex box
 *  so the inner filter ScrollView bounds and scrolls (GlobalSearchModal pattern).
 */
const MODAL_BODY_STYLE = { flexGrow: 1 } as const;
const MODAL_BODY_CONTENT_STYLE = { flex: 1 } as const;

/**
 * Renders the filter UI inside a full-screen Modal
 * @param p - Filter sheet props
 */
const renderModalPresentation = (p: MobileFilterSheetViewProps) => (
  <Modal isOpen={p.isOpen} onClose={p.onClose} size="lg">
    <ModalBackdrop />
    {/* Definite height (h-screen, full-screen) so the flex body/scroll chain
        resolves on iOS/Android: the gluestack ModalContent is a motion view and
        its ModalBody is a ScrollView, so a flex-1-only body collapses to 0 and
        the sheet renders empty (header + footer only). size="lg" (not "full") is
        required for the height to apply; w-full + max-w-full override the lg
        640px cap so the sheet is full-bleed (no side margin) on phones, and p-0
        drops the base 24px inset. Only the TOP safe-area inset is padded here so
        the header clears the status bar; the bottom inset lives on the footer so
        it reads as a bottom action bar, not a floating footer with a gap below.
        Mirrors the working GlobalSearchModal.
        SE-5548: at xl (≥1280 — the useMobileScreen desktop cut) the same
        modal presents as a centered bounded dialog instead of full-bleed:
        this is the desktop/iPad-landscape filter that replaced the legacy
        ModalFilter side panel. Definite xl:h-5/6 (not auto) keeps the
        flex body/scroll chain resolving on native iPad. */}
    <ModalContent
      className="h-screen w-full max-w-full rounded-none bg-surface-base p-0 xl:h-5/6 xl:w-1/3 xl:max-w-1/3 xl:rounded-lg"
      // eslint-disable-next-line no-restricted-syntax -- runtime safe-area top inset (p.topInset); no static token; ModalContent is a bare re-export accepting style pass-through
      style={{ paddingTop: p.topInset ?? 0 }}
    >
      <ModalHeader className="items-center justify-between border-b border-outline-default px-5">
        <Heading
          variant="title"
          className="font-heading text-title uppercase text-content-primary"
        >
          FILTERS
        </Heading>
        <Stack direction="horizontal" className="items-center gap-4">
          <Pressable
            onPress={p.onClearAll}
            disabled={p.activeFilterCount === 0}
            className={p.activeFilterCount > 0 ? "opacity-100" : "opacity-30"}
          >
            {/* SE-4146: "RESET ALL" per Figma (was "CLEAR ALL") */}
            <Text
              variant="body-bold"
              className="text-needs-semantic-info-600 dark:text-status-info"
            >
              RESET ALL
            </Text>
          </Pressable>
          <ModalCloseButton>
            <Icon as={X} size="md" className="text-content-subtle" />
          </ModalCloseButton>
        </Stack>
      </ModalHeader>
      {/* ModalBody is itself a ScrollView; used here as a non-scrolling flex box
          (scrollEnabled=false + flexGrow/flex content style) so the inner
          ScrollView can bound and scroll. min-h-0 lets both shrink below their
          content instead of pushing the footer off-screen. */}
      <ModalBody
        className="min-h-0 flex-1 p-0"
        scrollEnabled={false}
        contentContainerStyle={MODAL_BODY_CONTENT_STYLE}
        // eslint-disable-next-line no-restricted-syntax -- static flexGrow override className cannot win against gluestack ScrollView internal style; ModalBody is a bare re-export accepting style pass-through
        style={MODAL_BODY_STYLE}
      >
        <ScrollView
          className="min-h-0 flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerClassName="px-5 pb-4"
          keyboardShouldPersistTaps="handled"
        >
          {renderFilterSections(p)}
        </ScrollView>
      </ModalBody>
      <ModalFooter
        className="border-t border-outline-default px-5 py-3"
        // eslint-disable-next-line no-restricted-syntax -- runtime computed safe-area bottom inset (p.bottomInset + 16); no static token; ModalFooter is a bare re-export accepting style pass-through
        style={{ paddingBottom: (p.bottomInset ?? 0) + 16 }}
      >
        <Stack direction="horizontal" className="flex-1 items-center gap-2">
          <Pressable
            testID={p.discardTestID}
            onPress={p.onClose}
            className="items-center justify-center rounded-none border border-outline-strong px-6 py-2"
          >
            {/* SE-5082: "Discard" per Figma/AC (modal presentation = /search) */}
            <Text variant="body-bold" className="text-content-secondary">
              Discard
            </Text>
          </Pressable>
          <Pressable
            testID={p.showResultsTestID}
            onPress={p.onApply}
            disabled={p.applyDisabled}
            className={`flex-1 items-center justify-center rounded-none bg-surface-inverse py-2 ${
              p.applyDisabled ? "opacity-30" : "opacity-100"
            }`}
          >
            <Text
              variant="title-sm"
              numberOfLines={1}
              className="w-full text-center text-content-inverse"
            >
              {showResultsLabel(p.resultCount)}
            </Text>
          </Pressable>
        </Stack>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

/**
 * Renders the filter UI inside the shared MobileSheetChrome bottom sheet
 * (SE-5547). The chrome owns the sheet surface, height cap, drag handle, and
 * every dismissal path; this renderer supplies the CLEAR ALL header action,
 * the collapsible filter sections as the scrolling body, and the
 * Cancel / Show Results footer — the same slot shape as the already-migrated
 * MobileScoutReportFilterSheet.
 * @param p - Filter sheet props
 */
const renderActionsheetPresentation = (p: MobileFilterSheetViewProps) => (
  <MobileSheetChrome
    isOpen={p.isOpen}
    onClose={p.onClose}
    title="FILTERS"
    headerContent={
      /* CLEAR ALL — pinned with the title in the chrome's sticky header band */
      <Stack direction="horizontal" className="items-center justify-end">
        <Pressable
          onPress={p.onClearAll}
          disabled={p.activeFilterCount === 0}
          className={p.activeFilterCount > 0 ? "opacity-100" : "opacity-30"}
        >
          <Text
            variant="body-bold"
            className="text-needs-semantic-info-600 dark:text-status-info"
          >
            CLEAR ALL
          </Text>
        </Pressable>
      </Stack>
    }
    footer={
      /* Footer: Cancel + Show Results */
      <Stack direction="horizontal" className="items-center gap-2">
        <Pressable
          onPress={p.onClose}
          className="items-center justify-center rounded-none border border-outline-strong px-6 py-2"
        >
          <Text variant="body-bold" className="text-content-primary">
            Cancel
          </Text>
        </Pressable>
        <Pressable
          onPress={p.onApply}
          className="flex-1 items-center justify-center rounded-none bg-surface-inverse py-2"
        >
          <Text
            variant="title-sm"
            numberOfLines={1}
            className="w-full text-center text-content-inverse"
          >
            {showResultsLabel(p.resultCount)}
          </Text>
        </Pressable>
      </Stack>
    }
  >
    {renderFilterSections(p)}
  </MobileSheetChrome>
);

/** Lookup table mapping presentation mode to renderer */
const PRESENTATION_RENDERERS: Record<
  FilterSheetPresentation,
  (p: MobileFilterSheetViewProps) => React.ReactElement
> = {
  modal: renderModalPresentation,
  actionsheet: renderActionsheetPresentation,
};

/**
 * Renders the mobile filter UI in the requested presentation mode.
 * @param props - Filter sheet display data and callbacks
 */
const MobileFilterSheetView = (props: MobileFilterSheetViewProps) =>
  PRESENTATION_RENDERERS[props.presentation ?? "actionsheet"](props);

MobileFilterSheetView.displayName = "MobileFilterSheetView";

export default memo(MobileFilterSheetView);
